import React from 'react';

import { observer } from 'mobx-react';
import { TimeService } from '@bringg/frontend-utils';
import { TypographyBodySize, TypographyColor, TypographyH4, TypographyP } from '@bringg-frontend/ui';

import { DeliveryBlocksEvent } from '../../delivery-blocks-calendar-view';
import { DELIVERY_BLOCK_TIME_FORMAT } from '../../../modal/form/delivery-block-form';
import DeliveryBlockUtilizationMetrics from '../utilization-metrics/delivery-block-utilization-metrics';
import { getTotalOrdersCount } from 'bringg-web/features/delivery-blocks-v2/utils/delivery-block-utilization';
import { useHasCombinedOptimization } from 'bringg-web/features/delivery-blocks-v2/utils/use-has-combined-optimization';
import TimezoneService from 'bringg-web/services/timezone/timezone-service';
import { useMerchantConfiguration } from 'bringg-web/recipes';

import styles from './delivery-block-event-popover-content.module.scss';

interface Props {
	title: string;
	event: DeliveryBlocksEvent;
}

const WEEKDAY_FORMAT = 'dddd';

const DeliveryBlockEventPopoverContent: React.FC<Props> = observer(({ event, title }) => {
	const { hour_format: hoursFormat } = useMerchantConfiguration();
	const {
		start,
		end,
		teamTimezone,
		team,
		userIds,
		originalCapacity,
		deliveryBlocksResources = [],
		utilizationPercent
	} = event;
	const isCombinedOptimization = useHasCombinedOptimization(team);
	const momentTz = TimezoneService.getMomentTimezone(teamTimezone);
	const blockStart = momentTz(start);
	const blockTime = `${blockStart.format(WEEKDAY_FORMAT)} ${TimeService.format(
		blockStart,
		DELIVERY_BLOCK_TIME_FORMAT,
		hoursFormat
	)} - ${TimeService.format(momentTz(end), DELIVERY_BLOCK_TIME_FORMAT, hoursFormat)}`;

	return (
		<div className={styles.popoverContent} data-test-id="delivery-block-event-popover">
			<TypographyH4 tag="span">{title}</TypographyH4>
			<TypographyP
				tag="span"
				size={TypographyBodySize.Small}
				color={TypographyColor.Secondary}
				data-test-id="delivery-block-popover-time"
			>
				{blockTime}
			</TypographyP>
			<TypographyP tag="span" size={TypographyBodySize.Small} color={TypographyColor.Secondary}>
				<DeliveryBlockUtilizationMetrics
					resourcesCount={isCombinedOptimization ? deliveryBlocksResources.length : userIds.length}
					maxResources={originalCapacity}
					ordersCount={getTotalOrdersCount(deliveryBlocksResources)}
					utilizationPercent={utilizationPercent}
					testIdPrefix="delivery-block-popover"
				/>
			</TypographyP>
		</div>
	);
});

export default DeliveryBlockEventPopoverContent;
